interface LayoutShiftSource {
  node?: Node | null;
}

interface LayoutShiftEntry extends PerformanceEntry {
  value: number;
  hadRecentInput: boolean;
  sources?: LayoutShiftSource[];
}

/**
 * 레이아웃 시프트 소스 노드를 Sentry 태그용 짧은 셀렉터(`tag#id.class`)로 요약한다.
 * Element가 아니면(텍스트 노드·null) 빈 문자열.
 */
export const describeShiftTarget = (node: Node | null | undefined): string => {
  if (!(node instanceof Element)) {
    return '';
  }

  const tag = node.tagName.toLowerCase();
  const id = node.id ? `#${node.id}` : '';
  // SVG 요소의 className은 SVGAnimatedString이라 string일 때만 사용
  const className =
    typeof node.className === 'string' ? node.className.trim() : '';
  const firstClass = className ? `.${className.split(/\s+/)[0]}` : '';

  return `${tag}${id}${firstClass}`;
};

let largestShiftValue = 0;
let largestShiftTarget = '';

/**
 * `layout-shift` 엔트리를 관찰해 가장 큰 단일 시프트의 소스 요소 셀렉터를 기록한다.
 * CLS 리포트에 "무엇이 흔들렸는지"를 붙여 attribution 공백을 메운다.
 * buffered:true라 관찰 시작 전(페이지 로드 직후)의 시프트도 포함한다.
 */
export const startLayoutShiftTracking = (): void => {
  if (typeof PerformanceObserver === 'undefined') {
    return;
  }

  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as LayoutShiftEntry[]) {
        if (entry.hadRecentInput || entry.value <= largestShiftValue) {
          continue;
        }

        const source = entry.sources?.find((candidate) => candidate.node);
        largestShiftValue = entry.value;
        largestShiftTarget = describeShiftTarget(source?.node);
      }
    });
    observer.observe({ type: 'layout-shift', buffered: true });
  } catch {
    // layout-shift 미지원 브라우저 — attribution 없이 진행
  }
};

/** 지금까지 관찰된 가장 큰 시프트의 소스 셀렉터 (없으면 빈 문자열). */
export const getLargestShiftTarget = (): string => largestShiftTarget;
